import type { UrlObject } from 'url';

import { API_DOMAIN, SCHEME } from '@commons/utils/constants';

// Type definition of the options for the `asAbsoluteURL` function.

type AsAbsoluteURLOptions = {
  /**
   * The domain to use (defaults to the API domain).
   */
  domain?: string;

  /**
   * HTTP or HTTPS.
   *
   * The default is based on the current environment.
   */
  scheme?: 'http' | 'https';
};

/**
 * Transforms a relative URL into an absolute URL.
 *
 * @param relativeURL - The relative URL to transform.
 * @param options - The options to use when transforming the URL.
 *
 * @returns The absolute URL.
 */

export const asAbsoluteURL = (relativeURL = '', options: AsAbsoluteURLOptions = {}) => {
  const url = relativeURL.toString();

  // Why are you like this?
  if (url.startsWith('http')) return url;

  // Add slash if URL is missing it.
  const leadingSlash = !url.startsWith('/') ? '/' : '';

  // Set the options defaults.
  const { domain = API_DOMAIN, scheme = SCHEME } = options;

  return `${scheme}://${domain}${leadingSlash}${url}`;
};

/**
 * Check if a URL is external.
 *
 * @param url - a string containing a URL.
 *
 * @returns a boolean indicating if the URL is external.
 */

export const isExternalURL = (URL?: string | UrlObject) => {
  // Adding a fallback in case the value passed is `undefined`.
  const URLString = URL ? URL.toString() : '';

  return URLString.match(/^http|mailto/) !== null;
};

/**
 * Checks whether a menu URL has a link.
 */

export const hasMenuLinkUrl = (url?: string) => {
  const trimmedUrl = url?.trim();

  return Boolean(
    trimmedUrl && trimmedUrl !== '#' && trimmedUrl !== '<nolink>' && trimmedUrl !== 'route:<nolink>'
  );
};

/**
 * Remove the anchor from a URL.
 *
 * @param url - a string containing a URL.
 *
 * @returns a string containing the URL without the anchor.
 */

export const removeAnchor = (url: string) => {
  const anchor = url.split('#')[0];
  return anchor;
};

/**
 * Replace a query parameter in the current browser URL without navigating.
 *
 * Pass `null` as the value to remove the parameter.
 */

export const replaceURLSearchParam = (name: string, value: string | null) => {
  const url = new URL(window.location.href);

  if (value !== null) {
    url.searchParams.set(name, value);
  } else {
    url.searchParams.delete(name);
  }

  window.history.replaceState(null, '', url);
};
